import { useCallback, useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "../context/AlertContext";
import { useResearch } from "../context/ResearchContext";
import { deepResearchApi } from "../pages/DeepResearch/api";
import { ResearchStatus, StreamingMessage } from "../pages/DeepResearch/types";

export const useResearchActions = (uuid?: string) => {
  const { t } = useTranslation();
  const { setAlert } = useAlert();
  const {
    research,
    setResearch,
    progressState,
    updateProgress,
    resetProgress,
    activeTab,
    setActiveTab,
    editingPlan,
    setEditingPlan,
    approving,
    setApproving,
    loading,
    setLoading,
    error,
    setError,
    triggerForceUpdate,
  } = useResearch();

  const previousStatusRef = useRef<string>("");
  const alertTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Alert表示の確実な実行
  const showAlert = useCallback(
    (type: string, message: string) => {
      // 既存のタイマーをクリア
      if (alertTimeoutRef.current) {
        clearTimeout(alertTimeoutRef.current);
      }

      // 即座に表示を試行
      setAlert(type, message);

      // フォールバックとして遅延実行も設定
      alertTimeoutRef.current = setTimeout(() => {
        setAlert(type, message);
      }, 50);
    },
    [setAlert]
  );

  // 調査データの読み込み
  const loadResearch = useCallback(async () => {
    if (!uuid) return;

    setLoading(true);
    try {
      const response = await deepResearchApi.getResearchDetail(uuid);
      setResearch(response.data);
      updateProgress({
        currentProgress: response.data.progress_percentage || 0,
      });
    } catch (error) {
      setError(t("deepResearch.errors.reportFetchFailed"));
      console.error("Failed to load research:", error);
    } finally {
      setLoading(false);
    }
  }, [uuid, setLoading, setResearch, setError, updateProgress, t]);

  // 計画編集
  const editResearchPlan = useCallback(
    async (editQuery: string) => {
      if (!uuid || !editQuery.trim()) return;

      try {
        showAlert("info", t("deepResearch.success.planEditStarted"));

        const response = await deepResearchApi.editResearchPlan(uuid, { edit_instruction: editQuery });

        setResearch((prev) =>
          prev
            ? {
                ...prev,
                plan_text: response.data.plan_text,
                edit_history: [...(prev.edit_history || []), { query: editQuery, timestamp: new Date().toISOString() }],
              }
            : null
        );
        triggerForceUpdate();

        showAlert("success", t("deepResearch.success.planEdited"));
      } catch (error) {
        setError(t("deepResearch.errors.planEditFailed"));
        console.error("Failed to edit plan:", error);
        showAlert("error", t("deepResearch.errors.planEditFailed"));
        throw error;
      }
    },
    [uuid, showAlert, setResearch, setError, triggerForceUpdate, t]
  );

  // 計画承認と調査継続
  const approvePlanAndContinue = useCallback(async () => {
    if (!uuid) return;

    setApproving(true);

    try {
      await deepResearchApi.continueResearchWithApprovedPlan(
        uuid,
        undefined,
        (message: StreamingMessage) => {
          if (message.type === "progress") {
            const newProgress = {
              currentProgress: message.progress || 0,
              currentMessage: message.message || "",
              currentStatus: message.status || progressState.currentStatus,
              searchCount: message.search_count || progressState.searchCount,
              maxSearches: message.max_searches || progressState.maxSearches,
              currentQuery: message.query || progressState.currentQuery,
            };

            updateProgress(newProgress);

            setResearch((prev) => {
              if (!prev) return null;
              const updated = {
                ...prev,
                status: (message.status as ResearchStatus) || prev.status,
                progress_percentage: message.progress || 0,
              };
              return updated;
            });

            triggerForceUpdate();
          } else if (message.type === "completed") {
            // 完了時に進捗を100%に設定
            updateProgress({
              currentProgress: 100,
              currentMessage: message.message || "",
              currentStatus: "completed",
            });
            
            loadResearch();
            triggerForceUpdate();
          } else if (message.type === "status") {
            // ステータス更新時に進捗メッセージも更新
            const newProgress = {
              currentProgress: message.progress || progressState.currentProgress,
              currentMessage: message.message || progressState.currentMessage,
              currentStatus: message.status || progressState.currentStatus,
              searchCount: progressState.searchCount,
              maxSearches: progressState.maxSearches,
              currentQuery: progressState.currentQuery,
            };

            updateProgress(newProgress);

            if (message.status) {
              setResearch((prev) =>
                prev
                  ? {
                      ...prev,
                      status: message.status as ResearchStatus,
                      progress_percentage: message.progress || prev.progress_percentage,
                    }
                  : null
              );
              triggerForceUpdate();
            }
          } else if (message.type === "plan_created") {
            setResearch((prev) =>
              prev
                ? {
                    ...prev,
                    status: "plan_pending" as ResearchStatus,
                  }
                : null
            );
            triggerForceUpdate();
          } else if (message.type === "error") {
            console.error("Stream error:", message.message);
          }
        },
        (error: Error) => {
          console.error("Streaming error occurred:", error);
          setError(t("deepResearch.errors.errorOccurred", { message: error.message }));
          setApproving(false);
          triggerForceUpdate();
        },
        () => {
          setApproving(false);
          setEditingPlan(false);
          loadResearch();
          triggerForceUpdate();
        }
      );
    } catch (error) {
      setError(t("deepResearch.errors.startFailed"));
      console.error("Failed to approve plan:", error);
      setApproving(false);
      showAlert("error", t("deepResearch.errors.startFailed"));
      triggerForceUpdate();
    }
  }, [
    uuid,
    setApproving,
    updateProgress,
    progressState,
    setResearch,
    setError,
    setEditingPlan,
    triggerForceUpdate,
    loadResearch,
    t,
  ]);

  // 調査エクスポート
  const exportResearch = useCallback(
    async (format: "markdown" | "pdf" | "word" | "txt" = "markdown") => {
      if (!uuid || !research) return;

      try {
        const response = await deepResearchApi.exportResearch(uuid, { format });

        if (format === "word" && response.data.is_binary) {
          // Word形式の場合：Base64デコードしてバイナリファイルとしてダウンロード
          const binaryString = atob(response.data.content);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          const blob = new Blob([bytes], {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          });

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = response.data.filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          // その他の形式：通常のダウンロード処理
          const blob = new Blob([response.data.content], { type: response.data.content_type });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = response.data.filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }

        showAlert("success", t("deepResearch.success.downloaded", { format }));
      } catch (error) {
        console.error(`Failed to export research in ${format} format:`, error);
        showAlert("error", t("deepResearch.errors.downloadFailed"));
      }
    },
    [uuid, research, showAlert, t]
  );

  // ダウンロードメニューの表示/非表示状態
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const downloadMenuOpen = Boolean(downloadMenuAnchor);

  const handleDownloadMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(event.currentTarget);
  }, []);

  const handleDownloadMenuClose = useCallback(() => {
    setDownloadMenuAnchor(null);
  }, []);

  const handleDownloadFormat = useCallback(
    (format: "markdown" | "pdf" | "word" | "txt") => {
      exportResearch(format);
      handleDownloadMenuClose();
    },
    [exportResearch, handleDownloadMenuClose]
  );

  // ステータス変化監視とタブ切り替え
  useEffect(() => {
    if (!research) return;

    const prevStatus = previousStatusRef.current;
    const currentStatus = research.status;

    // ステータス変化処理（初回も含む）
    if (prevStatus !== currentStatus) {
      // 初回のanalyzing状態の通知（新しい調査開始）
      if (prevStatus === "" && currentStatus === "analyzing") {
        showAlert("info", t("deepResearch.success.researchStarted"));
      }

      // 計画生成開始の通知
      else if (prevStatus === "analyzing" && currentStatus === "planning") {
        showAlert("info", t("deepResearch.process.planGeneration"));
      }

      // 計画完成の通知とタブ切り替え
      else if (prevStatus === "planning" && currentStatus === "plan_pending") {
        showAlert("success", t("deepResearch.success.planCreated"));

        // 調査計画タブに切り替え（遅延実行）
        setTimeout(() => {
          // タブ数を計算（調査情報 + 情報源(あれば) + 調査計画）
          let tabIndex = 1; // 調査情報タブの次
          if (research.sources && research.sources.length > 0) {
            tabIndex++; // 情報源タブがある場合は次
          }
          setActiveTab(tabIndex);
        }, 300);
      }

      // 調査開始の通知とタブ切り替え
      else if (prevStatus === "plan_pending" && currentStatus === "researching") {
        showAlert(
          "info",
          t("deepResearch.process.researchModeStarting", { mode: research.research_mode || "detailed" })
        );

        // 調査情報タブに切り替え（遅延実行）
        setTimeout(() => {
          setActiveTab(0);
        }, 300);
      }

      // 調査完了の通知
      else if ((prevStatus === "researching" || prevStatus === "generating") && currentStatus === "completed") {
        showAlert("success", t("deepResearch.success.researchCompleted"));
      }

      previousStatusRef.current = currentStatus;
    }
  }, [research, editingPlan, showAlert, setActiveTab, t]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (alertTimeoutRef.current) {
        clearTimeout(alertTimeoutRef.current);
      }
    };
  }, []);

  return {
    // State
    research,
    progressState,
    activeTab,
    editingPlan,
    approving,
    loading,
    error,

    // Actions
    loadResearch,
    editResearchPlan,
    approvePlanAndContinue,
    exportResearch,
    showAlert,

    // Download menu state and handlers
    downloadMenuAnchor,
    downloadMenuOpen,
    handleDownloadMenuOpen,
    handleDownloadMenuClose,
    handleDownloadFormat,

    // 削除機能
    deleteResearch: useCallback(async () => {
      if (!uuid || !research) return false; // researchがない場合はfalseを返すように変更

      // window.confirmを削除
      // if (!window.confirm(t("deepResearch.confirmations.deleteResearch"))) return;

      try {
        await deepResearchApi.deleteResearch(uuid);
        showAlert("success", t("deepResearch.success.deleted"));
        return true; // 削除成功
      } catch (error) {
        console.error("Failed to delete research:", error);
        showAlert("error", t("deepResearch.errors.deleteFailed"));
        return false; // 削除失敗
      }
    }, [uuid, research, showAlert, t]),

    // Setters
    setActiveTab,
    setEditingPlan,
    resetProgress,
    triggerForceUpdate,
  };
};
